var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o;
import { detect } from 'detect-browser';
import produce from 'immer';
import { createPrettyKeyCombination } from '../createPrettyKeyCombination';
const browser = detect();
/** Whether current device is an Apple device. */
export const isAppleDevice = /Mac|iPod|iPhone|iPad/.test((_a = browser === null || browser === void 0 ? void 0 : browser.os) !== null && _a !== void 0 ? _a : '');
/** Whether current device is a Windows device. */
export const isWindowsDevice = /^(Windows|win)/.test((_b = browser === null || browser === void 0 ? void 0 : browser.os) !== null && _b !== void 0 ? _b : '');
export const keyboardModifierKeyAliases = [
    // TODO: [CONV-288] Remove usage of $mod in Habla
    '$mod',
    '$metaOrCtrl',
    '$controlOrAlt',
];
export const keyboardPcModifierKeyCodes = [
    'Control',
    'Alt',
    'Shift',
];
export const keyboardMacModifierKeyCodes = [
    'Control',
    'Alt',
    'Shift',
    'Meta',
];
export const keyboardUniversalModifierKeyCodes = [
    ...keyboardModifierKeyAliases,
    'Alt',
    'Control',
    'Shift',
];
export const keyboardModifierKeyCodes = [
    'Alt',
    'Control',
    'Meta',
    'Shift',
];
export const keyboardExtendedModifierKeyCodes = [
    ...keyboardModifierKeyAliases,
    ...keyboardModifierKeyCodes,
];
/** Represents the normative order of modifier key combinations for PC devices. */
const keyboardPcModifierKeyOrder = [
    'Control',
    'Alt',
    'Shift',
];
/**
 * Represents the normative order of modifier key combinations for Mac devices.
 *
 * @see https://developer.apple.com/design/human-interface-guidelines/inputs/keyboards/
 */
const keyboardMacModifierKeyOrder = [
    'Control',
    'Alt',
    'Shift',
    'Meta',
];
export const keyboardModifierKeyOrder = isAppleDevice
    ? keyboardMacModifierKeyOrder
    : keyboardPcModifierKeyOrder;
/** Represents various attributes of keyboard event keys. */
export const keyboardEventKeyAttributes = {
    ['`']: { description: 'Backquote' },
    ['-']: { description: 'Minus' },
    ['=']: { description: 'Equal' },
    ['[']: { description: 'Bracket left' },
    [']']: { description: 'Bracket right' },
    ['\\']: { description: 'Backslash' },
    [':']: { description: 'Colon' },
    [';']: { description: 'Semicolon' },
    [`'`]: { description: 'Quote' },
    [',']: { description: 'Comma' },
    ['.']: { description: 'Period' },
    ['/']: { description: 'Forward slash' },
    ['&']: { description: 'Ampersand' },
    ['"']: { description: 'Double quote' },
    ['(']: { description: 'Parenthesis left' },
    [')']: { description: 'Parenthesis right' },
    ['§']: { description: 'Section sign' },
    ['!']: { description: 'Exclamation mark' },
    ['$']: { description: 'Dollar sign' },
    ['#']: { description: 'Number sign' },
    ['^']: { description: 'Circumflex accent' },
    ['*']: { description: 'Asterisk' },
    ['@']: { description: 'Commercial at' },
    ['<']: { description: 'Less than sign' },
    ['>']: { description: 'Greater than sign' },
};
/** Represents various attributes of keyboard event codes. */
export const keyboardEventCodeAttributes = {
    // Modifiers
    $metaOrCtrl: isAppleDevice
        ? { label: '⌘', description: 'Command' }
        : { label: 'Ctrl' },
    $controlOrAlt: isAppleDevice
        ? { label: '⌥', description: 'Option' }
        : { label: 'Alt' },
    Meta: isAppleDevice
        ? { label: '⌘', description: 'Command' }
        : isWindowsDevice
            ? { label: 'Win' }
            : {},
    Alt: isAppleDevice ? { label: '⌥', description: 'Option' } : {},
    Control: isAppleDevice
        ? { label: '⌃', description: 'Control' }
        : { label: 'Ctrl' },
    Shift: isAppleDevice ? { label: '⇧', description: 'Shift' } : {},
    OS: isWindowsDevice ? { label: 'Win' } : {},
    // Row 1
    Backquote: {
        label: '`',
        description: (_c = keyboardEventKeyAttributes['`']) === null || _c === void 0 ? void 0 : _c.description,
    },
    Minus: {
        label: '-',
        description: (_d = keyboardEventKeyAttributes['-']) === null || _d === void 0 ? void 0 : _d.description,
    },
    Equal: {
        label: '=',
        description: (_e = keyboardEventKeyAttributes['=']) === null || _e === void 0 ? void 0 : _e.description,
    },
    Backspace: isAppleDevice ? { label: '⌫', description: 'Delete' } : {},
    // Row 2
    Tab: isAppleDevice ? { label: '⇥', description: 'Tab' } : { label: 'Tab' },
    BracketLeft: {
        label: '[',
        description: (_f = keyboardEventKeyAttributes['[']) === null || _f === void 0 ? void 0 : _f.description,
    },
    BracketRight: {
        label: ']',
        description: (_g = keyboardEventKeyAttributes[']']) === null || _g === void 0 ? void 0 : _g.description,
    },
    Backslash: {
        label: '\\',
        description: (_h = keyboardEventKeyAttributes['\\']) === null || _h === void 0 ? void 0 : _h.description,
    },
    // Row 3
    CapsLock: isAppleDevice
        ? { label: '⇪', description: 'Caps lock' }
        : { label: 'Caps lock' },
    Semicolon: {
        label: `;`,
        description: (_j = keyboardEventKeyAttributes[';']) === null || _j === void 0 ? void 0 : _j.description,
    },
    Quote: {
        label: `'`,
        description: (_k = keyboardEventKeyAttributes[`'`]) === null || _k === void 0 ? void 0 : _k.description,
    },
    Enter: isAppleDevice ? { label: '↵', description: 'Return' } : {},
    // Row 4
    IntlBackslash: { label: '\\', description: 'Intl Backslash' },
    Comma: {
        label: ',',
        description: (_l = keyboardEventKeyAttributes[',']) === null || _l === void 0 ? void 0 : _l.description,
    },
    Period: {
        label: '.',
        description: (_m = keyboardEventKeyAttributes['.']) === null || _m === void 0 ? void 0 : _m.description,
    },
    Slash: {
        label: '/',
        description: (_o = keyboardEventKeyAttributes['/']) === null || _o === void 0 ? void 0 : _o.description,
    },
    // Arrows
    ArrowDown: { label: '⏷', description: 'Arrow down' },
    ArrowLeft: { label: '⏴', description: 'Arrow left' },
    ArrowRight: { label: '⏵', description: 'Arrow right' },
    ArrowUp: { label: '⏶', description: 'Arrow up' },
    // Numpad
    NumpadDivide: { label: 'Numpad /', description: 'Numpad divide' },
    NumpadMultiply: { label: 'Numpad *', description: 'Numpad multiply' },
    NumpadSubtract: { label: 'Numpad -', description: 'Numpad subtract' },
    NumpadAdd: { label: 'Numpad +', description: 'Numpad add' },
    NumpadEnter: isAppleDevice
        ? { label: '⌤', description: 'Enter' }
        : { label: 'Numpad enter' },
    Numpad1: { label: 'Numpad 1' },
    Numpad2: { label: 'Numpad 2' },
    Numpad3: { label: 'Numpad 3' },
    Numpad4: { label: 'Numpad 4' },
    Numpad5: { label: 'Numpad 5' },
    Numpad6: { label: 'Numpad 6' },
    Numpad7: { label: 'Numpad 7' },
    Numpad8: { label: 'Numpad 8' },
    Numpad9: { label: 'Numpad 9' },
    Numpad0: { label: 'Numpad 0' },
    NumpadDecimal: { label: 'Numpad .', description: 'Numpad decimal' },
    NumpadEqual: { label: 'Numpad =', description: 'Numpad equal' },
    NumpadComma: { label: 'Numpad ,', description: 'Numpad comma' },
    // Other
    NumLock: isAppleDevice
        ? { label: '⌧', description: 'Num lock' }
        : { label: 'NumLock' },
    Space: isAppleDevice
        ? { label: '␣', description: 'Space' }
        : { label: 'Space' },
    Home: isAppleDevice ? { label: '↖', description: 'Home' } : {},
    End: isAppleDevice ? { label: '↘', description: 'End' } : {},
    PageUp: isAppleDevice
        ? { label: '⇞', description: 'Page up' }
        : { label: 'Page up' },
    PageDown: isAppleDevice
        ? { label: '⇟', description: 'Page down' }
        : { label: 'Page down' },
    Delete: isAppleDevice
        ? { label: '⌦', description: 'Delete' }
        : { label: 'Delete' },
    Escape: isAppleDevice
        ? { label: '⎋', description: 'Escape' }
        : { label: 'Esc', description: 'Escape' },
};
/**
 * Gets code to key map if the browser support it. The map can be used to
 * correctly determine the pressed key from `event.code`.
 *
 * @example
 *   // { 'KeyQ': 'q' } - US keyboard
 *   // { 'KeyQ : 'a' } - AZERTY keyboard
 */
export function getCodeKeyMap() {
    return __awaiter(this, void 0, void 0, function* () {
        if ('keyboard' in navigator) {
            try {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const layoutMap = yield navigator.keyboard.getLayoutMap();
                return Object.fromEntries(layoutMap.entries());
            }
            catch (_a) {
                return {};
            }
        }
        return {};
    });
}
/**
 * Gets key combinations where modifiers are normatively ordered.
 *
 * @param keyCombinations The key combinations to order.
 */
export function normativeOrderKeyCombinations(keyCombinations) {
    return keyCombinations.map(normativeOrderKeyCombination);
}
/**
 * Gets key combination where modifiers are normatively ordered.
 *
 * @param keyCombination The key combination to order.
 */
export function normativeOrderKeyCombination(keyCombination) {
    const sortBase = (base) => {
        if (!base) {
            return undefined;
        }
        const key = base[1];
        const modifiers = [...base[0]].sort((a, b) => keyboardModifierKeyOrder.indexOf(a) -
            keyboardModifierKeyOrder.indexOf(b));
        return [modifiers, key];
    };
    const binding = sortBase(keyCombination.binding);
    const presentation = sortBase(keyCombination.presentation);
    return { binding, presentation };
}
/**
 * Normalizes a list of key combinations by replacing aliases with OS specific
 * keys.
 *
 * @param keyCombinations The key combinations to normalize.
 */
export function normalizeKeyCombinations(keyCombinations) {
    return keyCombinations.map((keyCombination) => normalizeKeyCombination(keyCombination));
}
/**
 * Normalizes the key combination by replacing aliases with OS specific keys.
 *
 * @param keyCombination The key combination to normalize.
 */
export function normalizeKeyCombination(keyCombination) {
    return produce(keyCombination, (draft) => {
        draft.binding[0] = draft.binding[0].map((modifier) => { var _a; return (_a = normalizedModifierKeys[modifier]) !== null && _a !== void 0 ? _a : modifier; });
    });
}
const normalizedModifierKeys = {
    $controlOrAlt: isAppleDevice ? 'Control' : 'Alt',
    $metaOrCtrl: isAppleDevice ? 'Meta' : 'Control',
    $mod: isAppleDevice ? 'Meta' : 'Control',
};
/** Gets a human readable string of array of key combinations . */
export function keyCombinationsToString(keyCombinations) {
    return keyCombinations
        .map((keyCombination) => createPrettyKeyCombination(keyCombination)
        .toString()
        .replaceAll(',', ' '))
        .join(', ');
}
